import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { 
  getDispositivos, 
  getDispositivoById, 
  createDispositivo, 
  updateDispositivo, 
  deleteDispositivo 
} from '../apis/dispositivosApi';
import { DispositivoInterface } from '../interfaces/DispositivoInterface';
import { toast } from 'react-toastify';

export const useDispositivos = () => {
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const dispositivosQuery = useQuery<DispositivoInterface[], Error>({
    queryKey: ['dispositivos'],
    queryFn: () => getDispositivos(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  const createDispositivoMutation = useMutation({
    mutationFn: (dispositivo: Partial<DispositivoInterface>) => 
      createDispositivo(dispositivo, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispositivos'] });
      queryClient.invalidateQueries({ queryKey: ['tags-disponibles'] });
      toast.success('Dispositivo creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al crear dispositivo');
    },
  });

  const updateDispositivoMutation = useMutation({
    mutationFn: ({ id, dispositivo }: { id: number; dispositivo: Partial<DispositivoInterface> }) =>
      updateDispositivo(id, dispositivo, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispositivos'] });
      toast.success('Dispositivo actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al actualizar dispositivo');
    },
  });

  const deleteDispositivoMutation = useMutation({
    mutationFn: (id: number) => deleteDispositivo(id, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispositivos'] });
      queryClient.invalidateQueries({ queryKey: ['tags-disponibles'] });
      toast.success('Dispositivo eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al eliminar dispositivo');
    },
  });

  return {
    dispositivos: dispositivosQuery.data || [],
    isLoading: dispositivosQuery.isLoading,
    error: dispositivosQuery.error,
    createDispositivo: createDispositivoMutation.mutate,
    updateDispositivo: updateDispositivoMutation.mutate,
    deleteDispositivo: deleteDispositivoMutation.mutate,
    isCreating: createDispositivoMutation.isPending,
    isUpdating: updateDispositivoMutation.isPending,
    isDeleting: deleteDispositivoMutation.isPending,
  };
};

export const useDispositivo = (id: number) => {
  const { token } = useContext(AuthContext);

  return useQuery<DispositivoInterface, Error>({
    queryKey: ['dispositivo', id],
    queryFn: () => getDispositivoById(id, token!),
    enabled: !!token && !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
